import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { IdleLoopVideo } from './IdleLoopVideo';
import { apiClient } from '../../api/client';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AvALightbox — tap-to-enlarge modal for the AvA idle-loop animation.
 *
 * Two ways to feed media:
 *   1. Pass `idleLoopUrl` directly (chat already has it cached).
 *   2. Pass `avaId` and the component will fetch
 *      `/api/ava-video/idle-loop-for-ava/{avaId}` itself (used from
 *      dashboard cards where the URL is not pre-loaded).
 *
 * Always falls back to `avatarUrl` if no idle loop is available.
 */
const AvALightbox = ({
  open,
  onClose,
  name,
  avatarUrl,
  idleLoopUrl: providedIdleLoopUrl,
  avaId,
}) => {
  const [resolvedUrl, setResolvedUrl] = useState(providedIdleLoopUrl || null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (providedIdleLoopUrl) {
      setResolvedUrl(providedIdleLoopUrl);
      return;
    }
    if (!avaId) {
      setResolvedUrl(null);
      return;
    }
    let cancelled = false;
    setFetching(true);
    apiClient
      .request(`/api/ava-video/idle-loop-for-ava/${avaId}`)
      .then((data) => {
        if (cancelled) return;
        if (data?.idle_loop_url) {
          setResolvedUrl(`${API_URL}/api/files/${data.idle_loop_url}`);
        } else {
          setResolvedUrl(null);
        }
      })
      .catch(() => {
        if (!cancelled) setResolvedUrl(null);
      })
      .finally(() => {
        if (!cancelled) setFetching(false);
      });
    return () => { cancelled = true; };
  }, [open, providedIdleLoopUrl, avaId]);

  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9500] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="ava-lightbox-backdrop"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition"
        data-testid="ava-lightbox-close"
      >
        <X size={22} />
      </button>

      <div
        className="relative w-full max-w-sm sm:max-w-md aspect-square rounded-3xl overflow-hidden shadow-2xl ring-4 ring-white/10 bg-slate-900"
        onClick={(e) => e.stopPropagation()}
        data-testid="ava-lightbox-media"
      >
        {fetching && !resolvedUrl ? (
          <div className="w-full h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-white/70" />
          </div>
        ) : resolvedUrl ? (
          <IdleLoopVideo
            src={resolvedUrl}
            poster={avatarUrl}
            className="w-full h-full object-cover"
            testId="ava-lightbox-idle-loop"
          />
        ) : (
          <img
            src={avatarUrl}
            alt={name || 'AvA'}
            className="w-full h-full object-cover"
            data-testid="ava-lightbox-fallback-img"
          />
        )}
      </div>

      {name && (
        <p
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white font-semibold text-base sm:text-lg tracking-wide"
          data-testid="ava-lightbox-name"
        >
          {name}
        </p>
      )}
    </div>,
    document.body
  );
};

export { AvALightbox };
